import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import jwt_decode from "jwt-decode";
import "./requestTeamdesign.css";
import XlsxPopulate from "xlsx-populate/browser/xlsx-populate";

const RequestTeamList = () => {
  // =========================================================
  // State
  // =========================================================

  const [requests, setRequests] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    search: "",
    vertical: "",
    priority: "",
    hiring_type: "",
    working_mode: "",
    status: "",
  });

  const [depdata, setDepdata] = useState([]);
  const [selectedVertical, setSelectedVertical] = useState(null);
  const [loadingVertical, setLoadingVertical] = useState(false);

  // =========================================================
  // Pagination
  // =========================================================

  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  // =========================================================
  // Environment
  // =========================================================

  const SERVER_URL = process.env.REACT_APP_API_URL;
  const BASE_URL = process.env.REACT_APP_APP_URL;

  const token = JSON.parse(localStorage.getItem("key"));

  const tokenDecoded = jwt_decode(token.value);
  // console.log(tokenDecoded, "tokenDecoded========");
  const systemRole = tokenDecoded.systemRole ?? "";
  const userID = tokenDecoded.userId ?? "";

  // =========================================================
  // Total Pages
  // =========================================================

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  // =========================================================
  // Start Index
  // =========================================================

  const startIndex =
    totalRecords === 0 ? 0 : (currentPage - 1) * recordsPerPage;

  // =========================================================
  // End Index
  // =========================================================

  const endIndex = Math.min(currentPage * recordsPerPage, totalRecords);

  // =========================================================
  // Get Department List
  // =========================================================

  useEffect(() => {
    getDepartmentList();
  }, []);

  const getDepartmentList = async () => {
    try {
      setLoadingVertical(true);

      const response = await fetch(
        SERVER_URL + "api/common/department_group_list",
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token?.value}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const data = await response.json();

      setDepdata(data?.result || []);
    } catch (error) {
      console.error("Error fetching vertical list:", error);
    } finally {
      setLoadingVertical(false);
    }
  };

  // =========================================================
  // Vertical Options
  // =========================================================

  const Vertical_option = useMemo(() => {
    return (
      depdata?.map((row) => ({
        value: row.id,
        label: row.name,
      })) || []
    );
  }, [depdata]);

  // =========================================================
  // Get Request Team Data
  // =========================================================

  const fetchRequests = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();

      // Pagination
      params.append("role", systemRole);
      params.append("user_id", userID);
      params.append("page", currentPage);
      params.append("pageSize", recordsPerPage);

      // Filters
      if (filters.search) {
        params.append("search", filters.search);
      }

      if (filters.vertical) {
        params.append("vertical", filters.vertical);
      }

      if (filters.priority) {
        params.append("priority", filters.priority);
      }

      if (filters.hiring_type) {
        params.append("hiringType", filters.hiring_type);
      }

      if (filters.working_mode) {
        params.append("workingMode", filters.working_mode);
      }

      if (filters.status) {
        params.append("status", filters.status);
      }

      const apiUrl = `${SERVER_URL}api/requestTeam/list?${params.toString()}`;

      console.log("REQUEST TEAM API:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.value}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();

      console.log("REQUEST TEAM RESPONSE:", result);

      setRequests(result.data || []);

      setTotalRecords(Number(result.total || 0));
    } catch (error) {
      console.error("Error loading requests:", error);

      setRequests([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // Fetch when pagination/filter changes
  // =========================================================

  useEffect(() => {
    fetchRequests();
  }, [
    currentPage,
    recordsPerPage,
    filters.search,
    filters.vertical,
    filters.priority,
    filters.hiring_type,
    filters.working_mode,
    filters.status,
  ]);

  // =========================================================
  // Filter Change - Input
  // =========================================================

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Always go back to page 1
    setCurrentPage(1);
  };

  // =========================================================
  // Select Change
  // =========================================================

  const handleSelectChange = (option, name) => {
    const value = option?.value || "";

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "vertical") {
      setSelectedVertical(value);
    }

    // Reset page
    setCurrentPage(1);
  };

  // =========================================================
  // Clear Filters
  // =========================================================

  const clearFilters = () => {
    setFilters({
      search: "",
      vertical: "",
      priority: "",
      hiring_type: "",
      working_mode: "",
      status: "",
    });

    setSelectedVertical(null);

    setCurrentPage(1);
  };

  // =========================================================
  // Page Change
  // =========================================================

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  // =========================================================
  // Records Per Page
  // =========================================================

  const handleRecordsPerPageChange = (e) => {
    const newPageSize = Number(e.target.value);

    setRecordsPerPage(newPageSize);

    // Reset to page 1
    setCurrentPage(1);
  };

  // =========================================================
  // Delete
  // =========================================================

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this request?")) {
      return;
    }

    try {
      const response = await fetch(SERVER_URL + `api/requestTeam/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.value}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Unable to delete request.");
      }

      alert(data.message || "Request deleted successfully.");

      // If deleting the last record on current page,
      // move to previous page
      if (requests.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchRequests();
      }
    } catch (error) {
      console.error("Delete error:", error);

      alert(error.message || "Unable to delete request.");
    }
  };

  // =========================================================
  // Status Badge
  // =========================================================

  const getStatusClass = (status) => {
    switch (status) {
      case "Open":
        return "status-badge status-open";

      case "Sourcing":
        return "status-badge status-sourcing";

      case "Screening":
        return "status-badge status-screening";

      case "Interview Scheduled":
        return "status-badge status-interview_scheduled";

      case "Interview Completed":
        return "status-badge status-interview_completed";

      case "Selected":
        return "status-badge status-selected";

      case "Offer Released":
        return "status-badge status-offer-released";

      case "Offer Accepted":
        return "status-badge status-offer-accepted";

      case "Joined":
        return "status-badge status-joined";

      case "Rejected":
        return "status-badge status-rejected";

      case "On Hold":
        return "status-badge status-on-hold";

      default:
        return "status-badge";
    }
  };

  // =========================================================
  // Page Numbers
  // =========================================================

  const getPageNumbers = () => {
    const pages = [];

    // No pages
    if (totalPages <= 0) {
      return pages;
    }

    // 1 - 5 pages
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }

      return pages;
    }

    // Current page near beginning
    if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, "...", totalPages);

      return pages;
    }

    // Current page near end
    if (currentPage >= totalPages - 2) {
      pages.push(
        1,
        "...",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      );

      return pages;
    }

    // Current page in middle
    pages.push(
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    );

    return pages;
  };
  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      // Pagination
      params.append("role", systemRole);
      params.append("user_id", userID);
      params.append("page", currentPage);
      params.append("pageSize", recordsPerPage);

      // Filters
      if (filters.search) {
        params.append("search", filters.search);
      }

      if (filters.vertical) {
        params.append("vertical", filters.vertical);
      }

      if (filters.priority) {
        params.append("priority", filters.priority);
      }

      if (filters.hiring_type) {
        params.append("hiringType", filters.hiring_type);
      }

      if (filters.working_mode) {
        params.append("workingMode", filters.working_mode);
      }

      if (filters.status) {
        params.append("status", filters.status);
      }

      // Call Node API
      const response = await fetch(
        `${SERVER_URL}api/HiringTeam/export-data?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token.value}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch export data");
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Failed to export data");
      }

      const data = result.data || [];

      // =====================================================
      // CREATE EXCEL
      // =====================================================

      const workbook = await XlsxPopulate.fromBlankAsync();

      const sheet = workbook.sheet(0);

      sheet.name("Hiring Team");

      // =====================================================
      // HEADERS
      // =====================================================

      const headers = [
        "Profile Details",
        "Vertical",
        "Priority",
        "Status",
        "Location",
        "Skills",
        "Exp Range in Years",
        "Budget(Approx)/Monthly",
        "Budget(Approx)/Annum",
        "Position Open date",
        "Aging Days",
        "Aging Range",
        "Hiring Type",
        "Replacement for",
        "Experience",
        "Recruiter",
        "Offered Date",
        "Offered Month",
        "Offer Amount (Monthly)",
        "Bonus",
        "Offered Amount(Yearly)",
        "Offered candidate name",
        "Date of Joining",
        "Joining Month",
        "Mode of Employment",
        "Contract Duration",
        "Contact",
        "Email ID",
        "Remarks",
        "Sourcing Mode",
        "Date of Birth",
        "Working Mode",
        "Employee Code",
      ];

      sheet.range("A1:AH1").value([headers]);

      // =====================================================
      // DATA
      // =====================================================

      const rows = data.map((item) => [
        item.profile_details || "",
        item.vertical || "",
        item.priority || "",
        item.hiring_status || "",
        item.location || "",
        item.skills || "",
        item.min_range || "",
        item.budget_monthly || "",
        item.budget_annual || "",
        item.position_open_date || "",
        item.aging_days || "",
        item.aging_range || "",
        item.hiring_type || "",
        item.replacement_for || "",
        item.experience || "",
        item.recruiter || "",
        item.offered_date || "",
        item.offered_month || "",
        item.offered_amount_monthly || "",
        item.bonus || "",
        item.offered_amount_yearly || "",
        item.candidate_name || "",
        item.date_of_joining || "",
        item.joining_month || "",
        item.mode_of_employment || "",
        item.contract_duration || "",
        item.contact || "",
        item.email || "",
        item.remarks || "",
        item.sourcing_mode || "",
        item.date_of_birth || "",
        item.working_mode || "",
        item.employee_code || "",
      ]);

      if (rows.length > 0) {
        sheet.range(`A2:AH${rows.length + 1}`).value(rows);
      }

      // =====================================================
      // STYLE
      // =====================================================

      sheet.range("A1:AH1").style({
        fill: "1F4E78",
        fontColor: "FFFFFF",
        bold: true,
        horizontalAlignment: "center",
        verticalAlignment: "center",
        wrapText: true,

        border: {
          top: {
            style: "thin",
            color: "FFFFFF",
          },
          bottom: {
            style: "thin",
            color: "FFFFFF",
          },
          left: {
            style: "thin",
            color: "FFFFFF",
          },
          right: {
            style: "thin",
            color: "FFFFFF",
          },
        },
      });

      if (rows.length > 0) {
        sheet.range(`A2:AH${rows.length + 1}`).style({
          verticalAlignment: "top",
          wrapText: true,
        });
      }

      // =====================================================
      // COLUMN WIDTH
      // =====================================================

      const widths = [
        25, 18, 12, 18, 20, 30, 18, 22, 22, 18, 12, 18, 18, 22, 15, 20, 18, 18,
        22, 15, 22, 25, 18, 18, 22, 20, 18, 30, 30, 20, 18, 20, 18,
      ];

      widths.forEach((width, index) => {
        sheet.column(index + 1).width(width);
      });

      // =====================================================
      // FREEZE HEADER
      // =====================================================

      sheet.freezePanes(1, 0);

      // =====================================================
      // FILTER
      // =====================================================

      sheet.range(`A1:AH${Math.max(data.length + 1, 2)}`).autoFilter();

      // =====================================================
      // GENERATE FILE
      // =====================================================

      const blob = await workbook.outputAsync();

      // =====================================================
      // DOWNLOAD FILE
      // =====================================================

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = `Hiring_Team_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export Excel Error:", error);

      alert(error.message || "Failed to export Excel");
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // Render
  // =========================================================

  return (
    <div className="content_area">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="page-header">
        <h1>
          <Link className="btn_5 mr10 float-end" to="/requestForm?ongoingFrom">
            <i className="bi bi-plus-lg me-2"></i>
            Request Form
          </Link>
          Request List
        </h1>

        <ul id="breadcrumbs" className="breadcrumb mb00">
          <li>
            <a href="/dashboard">Dashboard</a>
          </li>

          <li className="current">
            <span>/</span> Request Team
          </li>
        </ul>
      </div>

      {/* =====================================================
          FILTER CARD
      ===================================================== */}

      <div className="row_bg">
        <div className="dash_white_box_ttl">Search</div>

        <div className="dash_white_box2 mb20 p20">
          <div className="row mb10">
            {/* Search */}
            <div className="col-lg-3 col-md-6 pr07">
              <label className="form-label">Search</label>

              <input
                className="form-control"
                type="text"
                name="search"
                placeholder="Search profile, skill, location..."
                value={filters.search}
                onChange={handleFilterChange}
              />
            </div>

            {/* Vertical */}
            <div className="col-lg-3 col-md-6 pr07">
              <label className="form-label">Vertical</label>

              <Select
                name="vertical"
                options={Vertical_option}
                value={
                  Vertical_option.find(
                    (option) =>
                      String(option.value) === String(filters.vertical),
                  ) || null
                }
                onChange={(option) => handleSelectChange(option, "vertical")}
                placeholder="Select Vertical"
                isSearchable
                isClearable
                isLoading={loadingVertical}
              />
            </div>

            {/* Priority */}
            <div className="col-lg-3 col-md-6 pr07">
              <label className="form-label">Priority</label>

              <select
                className="form-control"
                name="priority"
                value={filters.priority}
                onChange={handleFilterChange}
              >
                <option value="">All Priority</option>

                <option value="Critical">Critical</option>

                <option value="High">High</option>

                <option value="Medium">Medium</option>

                <option value="Low">Low</option>
              </select>
            </div>

            {/* Hiring Type */}
            <div className="col-lg-3 col-md-6 pr07">
              <label className="form-label">Hiring Type</label>

              <select
                className="form-control"
                name="hiring_type"
                value={filters.hiring_type}
                onChange={handleFilterChange}
              >
                <option value="">All Types</option>

                <option value="New Position">New Position</option>

                <option value="Replacement">Replacement</option>

                <option value="Contract">Contract</option>

                <option value="Intern">Intern</option>
              </select>
            </div>

            {/* Working Mode */}
            <div className="col-lg-3 col-md-6 pr07">
              <label className="form-label">Working Mode</label>

              <select
                className="form-control"
                name="working_mode"
                value={filters.working_mode}
                onChange={handleFilterChange}
              >
                <option value="">All Working Modes</option>

                <option value="Work From Office">Work From Office</option>

                <option value="Work From Home">Work From Home</option>

                <option value="Hybrid">Hybrid</option>
              </select>
            </div>

            {/* Status */}
            <div className="col-lg-3 col-md-6 pr07">
              <label className="form-label">Status</label>

              <select
                className="form-control"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Status</option>

                <option value="Open">Open</option>

                <option value="Sourcing">Sourcing</option>

                <option value="Screening">Screening</option>

                <option value="Interview Scheduled">Interview Scheduled</option>

                <option value="Interview Completed">Interview Completed</option>

                <option value="Selected">Selected</option>

                <option value="Offer Released">Offer Released</option>

                <option value="Offer Accepted">Offer Accepted</option>

                <option value="Joined">Joined</option>

                <option value="Rejected">Rejected</option>

                <option value="On Hold">On Hold</option>
              </select>
            </div>

            {/* Clear Filter */}
            <div className="col-lg-3 col-md-6 pr07">
              <label className="d-block mb30 pt07"></label>

              <div className="d-flex gap-2">
                <button className="btn_9 btn-danger" onClick={clearFilters}>
                  Clear Filters
                </button>

                <button
                  type="button"
                  className="btn_8 btn-success"
                  onClick={handleExportExcel}
                  disabled={loading}
                >
                  {loading ? (
                    "Exporting..."
                  ) : (
                    <>
                      <i className="fa fa-file-excel-o me-2"></i>
                      Export Excel
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ===================================================
            TABLE
        =================================================== */}

        <div className="row mb30">
          <div className="col-md-12 table-responsive">
            {/* Table Top */}
            <div className="table-top">
              <div>
                <strong>Request List</strong>

                <span className="record-count">{totalRecords} Records</span>
              </div>

              {/* Records Per Page */}
              <div className="records-per-page">
                <span>Show</span>

                <select
                  value={recordsPerPage}
                  onChange={handleRecordsPerPageChange}
                >
                  <option value="10">10</option>

                  <option value="25">25</option>

                  <option value="50">50</option>

                  <option value="100">100</option>
                </select>

                <span>entries</span>
              </div>
            </div>

            {/* =================================================
                TABLE
            ================================================= */}

            <table className="table_style_1">
              <thead>
                <tr>
                  <th>S.No.</th>

                  <th>Profile Details</th>

                  <th>Vertical</th>

                  <th>Priority</th>

                  <th>Location</th>

                  <th>Skills</th>

                  <th>Experience</th>

                  {Number(systemRole) === 2 && <th>Budget / Month</th>}

                  <th>Open Date</th>

                  <th>Hiring Type</th>

                  <th>Working Mode</th>

                  <th>Status</th>

                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {/* Loading */}
                {loading ? (
                  <tr>
                    <td colSpan="13" className="text-center loading-row">
                      Loading...
                    </td>
                  </tr>
                ) : requests.length === 0 ? (
                  /* Empty */
                  <tr>
                    <td colSpan="13" className="text-center empty-row">
                      <div>
                        <i className="bi bi-inbox"></i>

                        <p>No requests found</p>

                        <small>Try changing your filters</small>
                      </div>
                    </td>
                  </tr>
                ) : (
                  /* Records */
                  requests.map((item, index) => (
                    <tr key={item.id}>
                      {/* S.No */}
                      <td>{startIndex + index + 1}</td>

                      {/* Profile */}
                      <td>
                        <div className="profile-cell">
                          <strong>{item.profile_details}</strong>

                          <small>Request #{item.id}</small>
                        </div>
                      </td>

                      {/* Vertical */}
                      <td>{item.vertical}</td>

                      {/* Priority */}
                      <td>
                        <span
                          className={`priority-badge priority-${String(
                            item.priority,
                          ).toLowerCase()}`}
                        >
                          {item.priority}
                        </span>
                      </td>

                      {/* Location */}
                      <td>{item.location}</td>

                      {/* Skills */}
                      <td>
                        <div className="skills-cell">{item.skills}</div>
                      </td>

                      {/* Experience */}

                      <td>
                        {item.exp_min} - {item.exp_max} Years
                      </td>

                      {/* Budget */}
                      {Number(systemRole) === 2 && (
                        <td>
                          ₹{" "}
                          {Number(item.budget_month || 0).toLocaleString(
                            "en-IN",
                          )}
                        </td>
                      )}

                      {/* Open Date */}
                      <td>
                        {item.position_open_date
                          ? new Date(
                              item.position_open_date,
                            ).toLocaleDateString("en-GB")
                          : "-"}
                      </td>

                      {/* Hiring Type */}
                      <td>{item.hiring_type}</td>

                      {/* Working Mode */}
                      <td>{item.working_mode}</td>

                      {/* Status */}
                      <td>
                        <span className={getStatusClass(item.status)}>
                          {item.status || "Open"}
                        </span>
                      </td>

                      {/* Actions */}
                      <td>
                        <div className="action-buttons">
                          {/* View */}
                          <Link
                            title="View"
                            className="action-btn view"
                            to={`/requestViewForm/?id=${item.id}`}
                          >
                            <i className="fa fa-eye"></i>
                          </Link>

                          {/* Hiring */}
                          {(systemRole === 6 || systemRole === 2) && (
                            <Link
                              title="Hiring"
                              className="action-btn edit"
                              to={`/HiringForm?id=${item.id}`}
                            >
                              <i className="fa fa-users"></i>
                            </Link>
                          )}

                          {/* Delete */}
                          <button
                            title="Delete"
                            className="action-btn delete"
                            onClick={() => handleDelete(item.id)}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* =================================================
              PAGINATION
          ================================================= */}

          {totalRecords > 0 && (
            <div className="pagination-wrapper">
              {/* Pagination Info */}
              <div className="pagination-info">
                Showing <strong>{startIndex + 1}</strong> to{" "}
                <strong>{endIndex}</strong> of <strong>{totalRecords}</strong>{" "}
                entries
              </div>

              {/* Pagination */}
              <div className="pagination">
                {/* Previous */}
                <button
                  disabled={currentPage === 1 || loading}
                  onClick={() => goToPage(currentPage - 1)}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>

                {/* Page Numbers */}
                {getPageNumbers().map((page, index) => {
                  if (page === "...") {
                    return (
                      <span key={`dots-${index}`} className="pagination-dots">
                        ...
                      </span>
                    );
                  }

                  return (
                    <button
                      key={page}
                      disabled={loading}
                      className={currentPage === page ? "active" : ""}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  );
                })}

                {/* Next */}
                <button
                  disabled={currentPage === totalPages || loading}
                  onClick={() => goToPage(currentPage + 1)}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestTeamList;
